/**
 * 日志工具类
 * 
 * 安全修复：
 * 1. 生产环境不输出敏感信息
 * 2. 统一管理日志输出
 * 3. 支持日志级别控制
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private isDevelopment = import.meta.env.DEV;

  private shouldLog(level: LogLevel): boolean {
    // 生产环境只输出error级别
    if (!this.isDevelopment) {
      return level === 'error';
    }
    return true;
  }

  debug(...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      console.log('[DEBUG]', ...args);
    }
  }

  info(...args: unknown[]): void {
    if (this.shouldLog('info')) {
      console.info('[INFO]', ...args);
    }
  }

  warn(...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn('[WARN]', ...args);
    }
  }

  error(...args: unknown[]): void {
    if (this.shouldLog('error')) {
      console.error('[ERROR]', ...args);
      // 生产环境可以上报错误
      if (!this.isDevelopment) {
        this.reportError(args);
      }
    }
  }

  /**
   * 上报错误到监控系统
   */
  private reportError(_args: unknown[]): void {
    // TODO: 接入错误监控系统（如Sentry）
    // Sentry.captureException(args);
  }

  /**
   * 安全日志 - 不输出敏感信息
   */
  safe(level: LogLevel, message: string, data?: unknown): void {
    if (this.shouldLog(level)) {
      // 移除敏感字段
      const safeData = this.sanitize(data);
      console[level](`[${level.toUpperCase()}]`, message, safeData);
    }
  }

  /**
   * 移除敏感信息
   */
  private sanitize(data: unknown): unknown {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const sensitiveFields = [
      'password',
      'token',
      'secret',
      'apiKey',
      'accessToken',
      'refreshToken',
      'ownerId',
      'phone',
      'email',
      'idCard'
    ];

    const sanitized = { ...(data as Record<string, unknown>) };
    
    for (const field of sensitiveFields) {
      if (sanitized[field]) {
        sanitized[field] = '***';
      }
    }

    return sanitized;
  }
}

export const logger = new Logger();

// 导出便捷方法
export const log = {
  debug: (...args: unknown[]) => logger.debug(...args),
  info: (...args: unknown[]) => logger.info(...args),
  warn: (...args: unknown[]) => logger.warn(...args),
  error: (...args: unknown[]) => logger.error(...args),
  safe: (level: LogLevel, message: string, data?: unknown) => logger.safe(level, message, data),
};
